import type { CashflowBuilderConfig, CashflowRule, CashflowTemplateRow, FormulaDefinition } from "../types";

import { cashflowTemplateRows, getRowsForConfig } from "./cashflowTemplate";

function quoteLiteral(value: string): string {
  return `'${value.replaceAll("'", "''")}'`;
}

function quoteNullableText(value: string | null): string {
  return value === null ? "NULL::text" : quoteLiteral(value);
}

function formatNumber(value: number): string {
  return Number.isFinite(value) ? String(value) : "1";
}

function cleanRules(config: CashflowBuilderConfig): CashflowRule[] {
  return Object.values(config.rows)
    .flat()
    .filter((rule) => rule.debitValue.trim() && rule.creditValue.trim());
}

function getRowMeta(config: CashflowBuilderConfig, rowId: string) {
  return config.rowMeta[rowId];
}

function ruleValuesSql(config: CashflowBuilderConfig, rules: CashflowRule[]): string {
  if (rules.length === 0) {
    return `
  SELECT *
  FROM (
      VALUES (${quoteNullableText(null)}, ${quoteNullableText(null)}, ${quoteNullableText(null)}, ${quoteNullableText(
        null,
      )}, ${quoteNullableText(null)}, ${quoteNullableText(null)}, ${quoteNullableText(null)}, NULL::numeric, ${quoteNullableText(null)})
  ) AS v(row_id, property_id, property_name, match_type, dt_value, kt_value, coefficient, amt_side, dt_match_type, kt_match_type)
  WHERE FALSE`;
  }

  const values = rules
    .map((rule) => {
      const meta = getRowMeta(config, rule.rowId);
      const templateRow = cashflowTemplateRows.find((row) => row.rowId === rule.rowId);
      const displayCode = meta?.displayCode ?? templateRow?.displayCode ?? rule.rowId;
      const label = meta?.label ?? templateRow?.label ?? rule.rowId;
      
      // Determine match_type: 'both' if both sides use same type, otherwise 'both' (mixed is also 'both')
      const matchType = rule.dt_match_type === rule.kt_match_type ? rule.dt_match_type : 'both';

      return `(${quoteLiteral(rule.rowId)}, ${quoteLiteral(displayCode)}, ${quoteLiteral(
        label,
      )}, ${quoteLiteral(matchType)}, ${quoteLiteral(rule.debitValue.trim())}, ${quoteLiteral(rule.creditValue.trim())}, ${formatNumber(
        rule.coefficient,
      )}::numeric, ${quoteLiteral(rule.amountSide)}, ${quoteLiteral(rule.dt_match_type)}, ${quoteLiteral(rule.kt_match_type)})`;
    })
    .join(",\n      ");

  return `
  SELECT *
  FROM (
      VALUES
      ${values}
  ) AS v(row_id, property_id, property_name, match_type, dt_value, kt_value, coefficient, amt_side, dt_match_type, kt_match_type)`;
}

function templateValuesSql(config: CashflowBuilderConfig, rows: CashflowTemplateRow[]): string {
  if (rows.length === 0) {
    return `(${quoteNullableText(null)}, ${quoteNullableText(null)}, ${quoteNullableText(null)}, ${quoteNullableText(
      null,
    )}, NULL::numeric)`;
  }

  return rows
    .map((row) => {
      const meta = getRowMeta(config, row.rowId);
      return `(${quoteLiteral(row.rowId)}, ${quoteLiteral(meta?.displayCode ?? row.displayCode)}, ${quoteLiteral(
        meta?.label ?? row.label,
      )}, ${quoteLiteral(row.rowType)}, ${row.order})`;
    })
    .join(",\n      ");
}

function amountLookup(source: string, rowId: string): string {
  return `COALESCE((SELECT period_amount FROM ${source} WHERE row_id = ${quoteLiteral(rowId)}), 0::numeric)`;
}

function lookupRowValue(config: CashflowBuilderConfig, rowId: string): string {
  const formula = config.formulas[rowId];
  if (!formula) {
    return amountLookup("leaf_row_values", rowId);
  }

  if (formula.stage === 1) {
    return amountLookup("formula_level_1", rowId);
  }

  if (formula.stage === 2) {
    return amountLookup("formula_level_2", rowId);
  }

  return amountLookup("formula_level_3", rowId);
}

function buildFormulaExpression(config: CashflowBuilderConfig, formula: FormulaDefinition): string {
  const validReferences = formula.references.filter((rowId) => rowId.trim().length > 0);

  if (formula.kind === "diff") {
    const [left, right] = validReferences;
    const leftExpression = left ? lookupRowValue(config, left) : "0::numeric";
    const rightExpression = right ? lookupRowValue(config, right) : "0::numeric";
    return `${leftExpression} - ${rightExpression}`;
  }

  if (validReferences.length === 0) {
    return "0::numeric";
  }

  return validReferences.map((rowId) => lookupRowValue(config, rowId)).join(" + ");
}

function formulaStageSql(config: CashflowBuilderConfig, stage: 1 | 2 | 3): string {
  const rows = getRowsForConfig(config);
  const stagedRows = rows.filter((row) => row.rowType === "formula" && config.formulas[row.rowId]?.stage === stage);

  if (stagedRows.length === 0) {
    return "SELECT NULL::text AS row_id, NULL::numeric AS period_amount WHERE FALSE";
  }

  return stagedRows
    .map((row) => {
      const expression = buildFormulaExpression(config, config.formulas[row.rowId]!);
      return `SELECT ${quoteLiteral(row.rowId)} AS row_id, (${expression}) AS period_amount`;
    })
    .join("\n  UNION ALL\n  ");
}

function buildBaseSql(config: CashflowBuilderConfig): string {
  const allRules = cleanRules(config);
  const templateValues = templateValuesSql(config, getRowsForConfig(config));
  const rulesValues = ruleValuesSql(config, allRules);

  return `WITH params AS (
  SELECT
      DATE ${quoteLiteral(config.startDate)} AS start_date,
      DATE ${quoteLiteral(config.endDate)} AS end_date
),
cashflow_rules_mixed AS (${rulesValues}
),
report_template AS (
  SELECT *
  FROM (
      VALUES
      ${templateValues}
  ) AS v(row_id, property_id, property_name, row_type, row_order)
),
gl_lines AS (
  SELECT
      td.tran_id,
      td.tran_date,
      td.part_ser_num,
      td.dr_cr_type,
      td.gl_code,
      td.acct_num,
      COALESCE(td.eff_amt, td.tran_amt, 0) AS line_amt,
      td.post_date,
      td.tran_type,
      td.tran_sub_type
  FROM public.transaction_details td
  CROSS JOIN params p
  WHERE td.dr_cr_type IN ('DR', 'CR')
    AND COALESCE(td.del_flg, 'N') <> 'Y'
    AND td.tran_status = 'POSTED'
    AND td.tran_date >= p.start_date
    AND td.tran_date < p.end_date
),
debit_lines AS (
  SELECT
      g.tran_id,
      g.tran_date,
      g.part_ser_num AS dt_part_ser_num,
      g.gl_code AS dt_gl_code,
      g.acct_num AS dt_acct_num,
      g.line_amt AS dt_amount,
      g.post_date,
      g.tran_type,
      g.tran_sub_type
  FROM gl_lines g
  WHERE g.dr_cr_type = 'DR'
),
credit_lines AS (
  SELECT
      g.tran_id,
      g.tran_date,
      g.part_ser_num AS kt_part_ser_num,
      g.gl_code AS kt_gl_code,
      g.acct_num AS kt_acct_num,
      g.line_amt AS kt_amount,
      g.post_date,
      g.tran_type,
      g.tran_sub_type
  FROM gl_lines g
  WHERE g.dr_cr_type = 'CR'
),
matched_voucher_lines AS (
  SELECT
      r.row_id,
      r.property_id,
      r.property_name,
      r.dt_match_type || ' → ' || r.kt_match_type AS match_config,
      c.tran_id,
      c.tran_date,
      c.post_date,
      c.tran_type,
      c.tran_sub_type,
      r.amt_side AS configured_side,
      r.dt_value AS configured_debit_value,
      r.kt_value AS configured_credit_value,
      d.dt_gl_code,
      c.kt_gl_code,
      d.dt_acct_num,
      c.kt_acct_num,
      c.kt_part_ser_num,
      c.kt_amount AS base_amount,
      r.coefficient,
      (c.kt_amount * r.coefficient) AS contribution_amount
  FROM cashflow_rules_mixed r
  JOIN credit_lines c
    ON r.amt_side = 'CR'
   AND (
       (r.kt_match_type = 'gl_code' AND c.kt_gl_code = r.kt_value) OR
       (r.kt_match_type = 'acct_num' AND c.kt_acct_num = r.kt_value)
   )
  LEFT JOIN LATERAL (
      SELECT
          dx.dt_gl_code,
          dx.dt_acct_num
      FROM debit_lines dx
      WHERE dx.tran_id = c.tran_id
        AND dx.tran_date = c.tran_date
        AND (
            (r.dt_match_type = 'gl_code' AND dx.dt_gl_code = r.dt_value) OR
            (r.dt_match_type = 'acct_num' AND dx.dt_acct_num = r.dt_value)
        )
      ORDER BY dx.dt_part_ser_num
      LIMIT 1
  ) d ON TRUE
  WHERE EXISTS (
      SELECT 1
      FROM debit_lines dx
      WHERE dx.tran_id = c.tran_id
        AND dx.tran_date = c.tran_date
        AND (
            (r.dt_match_type = 'gl_code' AND dx.dt_gl_code = r.dt_value) OR
            (r.dt_match_type = 'acct_num' AND dx.dt_acct_num = r.dt_value)
        )
  )

  UNION ALL

  SELECT
      r.row_id,
      r.property_id,
      r.property_name,
      r.dt_match_type || ' → ' || r.kt_match_type AS match_config,
      d.tran_id,
      d.tran_date,
      d.post_date,
      d.tran_type,
      d.tran_sub_type,
      r.amt_side AS configured_side,
      r.dt_value AS configured_debit_value,
      r.kt_value AS configured_credit_value,
      d.dt_gl_code,
      c.kt_gl_code,
      d.dt_acct_num,
      c.kt_acct_num,
      c.kt_part_ser_num,
      d.dt_amount AS base_amount,
      r.coefficient,
      (d.dt_amount * r.coefficient) AS contribution_amount
  FROM cashflow_rules_mixed r
  JOIN debit_lines d
    ON r.amt_side = 'DR'
   AND (
       (r.dt_match_type = 'gl_code' AND d.dt_gl_code = r.dt_value) OR
       (r.dt_match_type = 'acct_num' AND d.dt_acct_num = r.dt_value)
   )
  LEFT JOIN LATERAL (
      SELECT
          cx.kt_gl_code,
          cx.kt_acct_num,
          cx.kt_part_ser_num
      FROM credit_lines cx
      WHERE cx.tran_id = d.tran_id
        AND cx.tran_date = d.tran_date
        AND (
            (r.kt_match_type = 'gl_code' AND cx.kt_gl_code = r.kt_value) OR
            (r.kt_match_type = 'acct_num' AND cx.kt_acct_num = r.kt_value)
        )
      ORDER BY cx.kt_part_ser_num
      LIMIT 1
  ) c ON TRUE
  WHERE EXISTS (
      SELECT 1
      FROM credit_lines cx
      WHERE cx.tran_id = d.tran_id
        AND cx.tran_date = d.tran_date
        AND (
            (r.kt_match_type = 'gl_code' AND cx.kt_gl_code = r.kt_value) OR
            (r.kt_match_type = 'acct_num' AND cx.kt_acct_num = r.kt_value)
        )
  )
),
row_contributions AS (
  SELECT
      row_id,
      property_id,
      property_name,
      SUM(contribution_amount) AS period_amount
  FROM matched_voucher_lines
  GROUP BY row_id, property_id, property_name
),
leaf_row_values AS (
  SELECT
      t.row_id,
      t.property_id,
      t.property_name,
      COALESCE(rc.period_amount, 0::numeric) AS period_amount
  FROM report_template t
  LEFT JOIN row_contributions rc
    ON rc.row_id = t.row_id
  WHERE t.row_type = 'leaf'
),
formula_level_1 AS (
  ${formulaStageSql(config, 1)}
),
formula_level_2 AS (
  ${formulaStageSql(config, 2)}
),
formula_level_3 AS (
  ${formulaStageSql(config, 3)}
),
formula_values AS (
  SELECT * FROM formula_level_1
  UNION ALL
  SELECT * FROM formula_level_2
  UNION ALL
  SELECT * FROM formula_level_3
)`;
}

export function buildCashflowDetailSql(config: CashflowBuilderConfig): string {
  return `${buildBaseSql(config)}
SELECT
    m.row_id,
    m.property_id,
    m.property_name,
    m.match_config,
    m.tran_id,
    m.tran_date,
    m.post_date,
    m.tran_type,
    m.tran_sub_type,
    m.configured_side,
    m.configured_debit_value,
    m.configured_credit_value,
    m.dt_gl_code,
    m.kt_gl_code,
    m.dt_acct_num,
    m.kt_acct_num,
    m.kt_part_ser_num,
    m.base_amount,
    m.coefficient,
    m.contribution_amount
FROM matched_voucher_lines m
ORDER BY m.post_date DESC NULLS LAST, m.tran_date DESC, m.tran_id DESC, m.kt_part_ser_num NULLS LAST;`;
}

export function buildCashflowSummarySql(config: CashflowBuilderConfig): string {
  return `${buildBaseSql(config)}
, final_report AS (
  SELECT
      t.row_id,
      t.property_id,
      t.property_name,
      t.row_type,
      t.row_order,
      CASE
          WHEN t.row_type = 'section' THEN NULL::numeric
          WHEN t.row_type = 'leaf' THEN COALESCE(l.period_amount, 0::numeric)
          ELSE COALESCE(f.period_amount, 0::numeric)
      END AS report_period_amount
  FROM report_template t
  LEFT JOIN leaf_row_values l
    ON l.row_id = t.row_id
  LEFT JOIN formula_values f
    ON f.row_id = t.row_id
)
SELECT
    row_id,
    property_id,
    property_name,
    report_period_amount AS "Тайлант үеийн дүн"
FROM final_report
ORDER BY row_order;`;
}
